<template>
  <div
    ref="centerRef"
    class="notification-center relative"
  >
    <!-- Notification Bell Button with OverlayBadge -->
    <!-- A real button: an <a> without href can't be reached by keyboard -->
    <button
      ref="bellButton"
      type="button"
      class="kb-bell"
      :aria-label="unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'"
      aria-haspopup="dialog"
      @click="toggle"
    >
      <OverlayBadge
        v-if="unreadCount > 0"
        :value="unreadCount > 9 ? '9+' : unreadCount.toString()"
        severity="warn"
        class="mr-2 lg:mr-0"
      >
        <i class="pi pi-bell text-base lg:!text-2xl leading-none" />
      </OverlayBadge>
      <i
        v-else
        class="pi pi-bell text-base lg:!text-2xl leading-none"
      />
      <span
        class="block lg:hidden font-medium ml-2"
        aria-hidden="true"
      >Notifications</span>
    </button>

    <!-- Notification Panel using PrimeVue Popover -->
    <Popover
      ref="popover"
      :show-close-icon="false"
      position="bottom"
      class="notifications-popover"
      @show="onPopoverShow"
    >
      <div class="p-0 fixed-width-panel">
        <!-- Panel Header -->
        <div class="kb-bell-head">
          <span class="font-semibold">
            Notifications
            <span
              v-if="unreadCount > 0"
              class="font-mono text-[12px] font-normal text-kb-gold ml-1"
            >{{ unreadCount }} unread</span>
          </span>
          <Button
            v-if="unreadCount > 0"
            text
            size="small"
            label="Mark all read"
            @click="handleMarkAllAsRead"
          />
        </div>

        <!-- Empty state when no notifications -->
        <p
          v-if="notifications.length === 0"
          class="py-10 text-center text-[13px] text-kb-muted m-0"
        >
          No notifications.
        </p>

        <!-- Notifications List -->
        <NotificationsList
          v-else
          max-height="350px"
          :compact="true"
          @notification-click="handleNotificationClick"
        />

        <!-- Panel Footer -->
        <div class="kb-bell-foot">
          <NuxtLink
            to="/notifications"
            class="kb-bell-all"
            @click="hidePopover"
          >
            All notifications
          </NuxtLink>
        </div>
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useNotifications } from '~/composables/useNotifications'
import type { Notification } from '~/shared/types/notification'

const centerRef = ref<HTMLElement | null>(null)
const bellButton = ref<HTMLElement | null>(null)
const popover = ref() // Reference to the Popover component

const {
  unreadCount,
  markAllAsRead,
  notificationsArray: notifications,
} = useNotifications()

// Toggle notification popover
const toggle = (event: Event) => {
  if (popover.value) {
    popover.value.toggle(event)
  }
}

// Call alignOverlay after popover is shown
const onPopoverShow = () => {
  // Allow DOM to update before realigning
  setTimeout(() => {
    if (popover.value) {
      popover.value.alignOverlay()
    }
  }, 0)
}

// Hide popover
const hidePopover = () => {
  if (popover.value) {
    popover.value.hide()
  }
}

// Handle notification click
const handleNotificationClick = (notification: Notification) => {
  // Use the useNotifications composable to handle the navigation
  const { handleNotificationClick: composableHandler } = useNotifications()
  composableHandler(notification)

  // Close the panel after clicking a notification
  hidePopover()
}

// Mark all as read
const handleMarkAllAsRead = async () => {
  await markAllAsRead()
}
</script>

<style scoped>
/* The bell in the top bar */
.kb-bell {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem;
  border: 0;
  border-radius: 2px;
  background: none;
  color: var(--kb-muted);
  cursor: pointer;
}

.kb-bell:hover {
  color: var(--kb-fg);
  background: var(--kb-hover);
}

.kb-bell:focus-visible {
  outline: 2px solid var(--kb-accent);
  outline-offset: 2px;
}

:deep(.p-popover) {
  max-width: 95vw;
}

:deep(.p-popover.p-popover-bottom .p-popover-arrow) {
  left: calc(var(--overlayArrowLeft, 0) + 12px) !important;
}

/* The panel: a header line, the list, a link to the full page */
.fixed-width-panel {
  width: 380px;
  max-width: calc(100vw - 2rem);
}

.kb-bell-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-height: 2.75rem;
  padding: 0.25rem 0.5rem 0.25rem 1rem;
  border-bottom: 1px solid var(--kb-line);
  font-size: 0.875rem;
  color: var(--kb-fg);
}

.kb-bell-foot {
  border-top: 1px solid var(--kb-line);
  text-align: center;
}

.kb-bell-all {
  display: block;
  padding: 0.625rem;
  font: 12.5px var(--kb-font-mono);
  color: var(--kb-muted);
  text-decoration: none;
}

.kb-bell-all:hover {
  color: var(--kb-accent);
}
</style>
